import type { CanonicalOrigin } from '@/lib/seo/canonical-origin';
import { getPublicUrl } from '@/lib/seo/canonical-origin';
import type { PublicUnitInfo } from '@/hooks/use-public-site';
import type { Page, Site } from '@/types/database';

export type RobotsDirective = 'index, follow' | 'noindex, nofollow';

export interface SeoMetadata {
  title: string;
  description: string | null;
  canonicalUrl: string;
  robots: RobotsDirective;
  ogImage: string | null;
}

// A page is only safe to index once every gate that makes it visible to a
// real anonymous visitor is open — mirrors the same fields SitePageChrome's
// nav filtering and the pages/blocks RLS policies already key off, so
// "indexable" never claims a page a crawler can't actually fetch content
// for. `page: null` covers the "coming soon" (zero published pages) state.
export function isPageIndexable(site: Site, page: Page | null): boolean {
  if (site.status !== 'published') return false;
  if (!page) return false;
  return page.is_published && page.visibility === 'public';
}

function generatedTitle(page: Page | null, unit: PublicUnitInfo): string {
  if (!page || page.is_home) return unit.name;
  return `${page.title} | ${unit.name}`;
}

function generatedDescription(unit: PublicUnitInfo): string | null {
  if (!unit.location) return null;
  return `${unit.name}, a Scouting unit based in ${unit.location}.`;
}

// Priority per page: custom page value, then the unit's own site-wide
// default, then a value templated from real unit data — never a fabricated
// description with no basis in what the unit actually entered.
export function resolveSeoMetadata(params: {
  site: Site;
  page: Page | null;
  unit: PublicUnitInfo;
  origin: CanonicalOrigin;
  pathname?: string;
}): SeoMetadata {
  const { site, page, unit, origin, pathname } = params;

  const title = page?.seo_title || site.seo_title || generatedTitle(page, unit);
  const description = page?.seo_description || site.seo_description || generatedDescription(unit);
  const canonicalUrl = getPublicUrl(origin, pathname);
  const robots: RobotsDirective = isPageIndexable(site, page) ? 'index, follow' : 'noindex, nofollow';

  return { title, description, canonicalUrl, robots, ogImage: site.logo_url };
}
